// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- ambient global.d.ts has no runtime JS counterpart to import
/// <reference path="./global.d.ts" />
"use client";

import { callbacks } from "./observer";

type ConsentDecision = number[];

type PrivacyManagerApiMessage = {
  PrivacyManagerAPI?: {
    capabilities?: object;
    action?: string;
  };
};

const PRIVACY_MANAGER_API_GLOBAL_KEY = "PrivacyManagerAPI";
const GET_GDPR_CONSENT_DECISION_METHOD = "getGDPRConsentDecision";
const GET_CONSENT_DECISION_MESSAGE_ACTION = "getConsentDecision";
const GDPR_PREF_ALLOWS_EVENT_PREFIX = "GDPR Pref Allows ";
const REQUIRED_CONSENT_CATEGORY = 1;
const FUNCTIONAL_CONSENT_CATEGORY = 2;
const ADVERTISING_CONSENT_CATEGORY = 3;

const notifyConsentObservers = (decision: ConsentDecision) => {
  if (decision.includes(REQUIRED_CONSENT_CATEGORY)) {
    callbacks.required();
  }
  if (decision.includes(FUNCTIONAL_CONSENT_CATEGORY)) {
    callbacks.functional();
  }
  if (decision.includes(ADVERTISING_CONSENT_CATEGORY)) {
    callbacks.advertising();
  }
};

const dispatchGdprLabels = (labels: ConsentDecision) => {
  const dispatched = new Set<number>();
  for (const label of labels) {
    if (!dispatched.has(label)) {
      globalThis.dataLayer?.push({
        event: GDPR_PREF_ALLOWS_EVENT_PREFIX + String(label),
      });
      dispatched.add(label);
    }
  }
};

const handleConsentDecision = (decision: ConsentDecision) => {
  notifyConsentObservers(decision);
  dispatchGdprLabels(decision);
};

let consentResolutionPromise: Promise<void> | undefined;

let trustedConsentOrigin: string | undefined;

export const setTrustedConsentOrigin = (origin: string) => {
  trustedConsentOrigin = origin;
};

const isAcceptedConsentMessageOrigin = (origin: string) =>
  origin === globalThis.location.origin || origin === trustedConsentOrigin;

const PRIVACY_MANAGER_API_POLL_MAX_CHECKS = 40;
const PRIVACY_MANAGER_API_POLL_MIN_INTERVAL_MS = 100;
const PRIVACY_MANAGER_API_POLL_MAX_INTERVAL_MS = 1000;
const PRIVACY_MANAGER_API_POLL_PLATEAU_CHECKS = 10;
const PRIVACY_MANAGER_API_POLL_GROWTH_FACTOR = 1.3;

const getPrivacyManagerApiPollIntervalMs = (checkNumber: number) => {
  const checksPastPlateau =
    checkNumber - PRIVACY_MANAGER_API_POLL_PLATEAU_CHECKS;
  if (checksPastPlateau <= 0) {
    return PRIVACY_MANAGER_API_POLL_MIN_INTERVAL_MS;
  }

  return Math.min(
    PRIVACY_MANAGER_API_POLL_MAX_INTERVAL_MS,
    PRIVACY_MANAGER_API_POLL_MIN_INTERVAL_MS *
      PRIVACY_MANAGER_API_POLL_GROWTH_FACTOR ** checksPastPlateau,
  );
};

const pollUntilPrivacyManagerApiIsAvailable = (
  maxChecks = PRIVACY_MANAGER_API_POLL_MAX_CHECKS,
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (Object.hasOwn(globalThis, PRIVACY_MANAGER_API_GLOBAL_KEY)) {
      resolve();
      return;
    }

    let checks = 0;
    const scheduleNextCheck = () => {
      setTimeout(() => {
        checks++;
        if (Object.hasOwn(globalThis, PRIVACY_MANAGER_API_GLOBAL_KEY)) {
          resolve();
        } else if (checks >= maxChecks) {
          reject(new Error("PrivacyManagerAPI never became available"));
        } else {
          scheduleNextCheck();
        }
      }, getPrivacyManagerApiPollIntervalMs(checks));
    };

    scheduleNextCheck();
  });

export const resolveConsentDecision = (): Promise<void> => {
  consentResolutionPromise ??= pollUntilPrivacyManagerApiIsAvailable().then(
    () =>
      new Promise<void>((resolve) => {
        const { consentDecision: existingDecision, source } =
          globalThis.PrivacyManagerAPI?.callApi(
            GET_GDPR_CONSENT_DECISION_METHOD,
            globalThis.location.host,
          ) ?? {};

        if (
          source === "asserted" &&
          existingDecision &&
          existingDecision.length > 0
        ) {
          handleConsentDecision(existingDecision);
          resolve();
          return;
        }

        // eslint-disable-next-line sonarjs/post-message -- origin restricted below to our own origin or the configured TrustArc consent origin
        globalThis.addEventListener("message", function handleMessage(event) {
          try {
            if (!isAcceptedConsentMessageOrigin(event.origin)) {
              return;
            }
            if (!event.data) {
              return;
            }
            const parsed = JSON.parse(
              event.data as string,
            ) as PrivacyManagerApiMessage;
            if (
              !parsed.PrivacyManagerAPI?.capabilities ||
              parsed.PrivacyManagerAPI.action !==
                GET_CONSENT_DECISION_MESSAGE_ACTION
            ) {
              return;
            }

            const { consentDecision: decision, source } =
              globalThis.PrivacyManagerAPI?.callApi(
                GET_GDPR_CONSENT_DECISION_METHOD,
                globalThis.location.host,
              ) ?? {};

            if (source !== "asserted" || !decision) {
              return;
            }
            handleConsentDecision(decision);
            resolve();
          } catch {
            // ignore
          }
        });

        globalThis.postMessage(
          JSON.stringify({
            PrivacyManagerAPI: {
              action: GET_CONSENT_DECISION_MESSAGE_ACTION,
              self: globalThis.location.host,
              timestamp: Date.now(),
            },
          }),
          globalThis.location.origin,
        );
      }),
  );
  consentResolutionPromise.catch(() => {
    consentResolutionPromise = undefined;
  });

  return consentResolutionPromise;
};
